import {
  createMutationKeys,
  createQueryKeys,
} from '@lukemorales/query-key-factory'
import { RequestTypes } from './requestTypes'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { createQueryKeyString } from '@core/react-query/queryParams'
import { PageResponse } from '@core/entities/pagination'
import { ICompetencyTemplate } from '@core/entities/competency/CompetencyTemplate'
import { ICompetencyTemplateVersion } from '@core/entities/competency/CompetencyTemplateVersion'

export const competencyTemplateKeys = createQueryKeys('competency-templates', {
  getTemplates: ({
    organisationId,
    status,
    page,
    size,
    term,
  }: RequestTypes['getCompetencyTemplates']) => {
    return {
      queryKey: [organisationId, status, page, size, term],
      queryFn: async () => {
        const queryString = createQueryKeyString({
          organisationId,
          status,
          page,
          size,
          term,
        })
        const { data } = await APIAxiosInstance.get(
          `competencies/admin/templates${queryString}`,
        )
        return data as PageResponse<ICompetencyTemplate>
      },
    }
  },
  getTemplate: ({ templateId }: RequestTypes['getCompetencyTemplate']) => {
    return {
      queryKey: [templateId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `competencies/admin/templates/${templateId}`,
        )
        return data as ICompetencyTemplate
      },
    }
  },
  getVersions: ({
    templateId,
    page,
    size,
  }: RequestTypes['getCompetencyTemplateVersions']) => {
    return {
      queryKey: [templateId, page, size],
      queryFn: async () => {
        const queryString = createQueryKeyString({ page, size })
        const { data } = await APIAxiosInstance.get(
          `competencies/admin/templates/${templateId}/versions${queryString}`,
        )
        return data as PageResponse<ICompetencyTemplateVersion>
      },
    }
  },
  getVersion: ({
    templateId,
    versionId,
  }: RequestTypes['getCompetencyTemplateVersion']) => {
    return {
      queryKey: [templateId, versionId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `competencies/admin/templates/${templateId}/versions/${versionId}`,
        )
        return data as ICompetencyTemplateVersion
      },
    }
  },
})

export const competencyTemplateMutationKeys = createMutationKeys(
  'competency-templates',
  {
    createTemplate: {
      mutationKey: null,
      mutationFn: async ({
        organisationId,
        ...payload
      }: RequestTypes['createCompetencyTemplate']) => {
        const { data } = await APIAxiosInstance.post(
          `competencies/admin/templates?organisationId=${organisationId}`,
          payload,
        )
        return data
      },
    },
    updateTemplate: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        ...payload
      }: RequestTypes['updateCompetencyTemplate']) => {
        const { data } = await APIAxiosInstance.put(
          `competencies/admin/templates/${templateId}`,
          payload,
        )
        return data
      },
    },
    archiveTemplate: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
      }: RequestTypes['archiveCompetencyTemplate']) => {
        const { data } = await APIAxiosInstance.put(
          `competencies/admin/templates/${templateId}/archive`,
        )
        return data
      },
    },
    createVersion: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
      }: RequestTypes['createCompetencyTemplateVersion']) => {
        const { data } = await APIAxiosInstance.post(
          `competencies/admin/templates/${templateId}/versions`,
        )
        return data
      },
    },
    updateVersionConfiguration: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        versionId,
        ...payload
      }: RequestTypes['updateCompetencyTemplateVersionConfiguration']) => {
        const { data } = await APIAxiosInstance.put(
          `competencies/admin/templates/${templateId}/versions/${versionId}/configuration`,
          payload,
        )
        return data
      },
    },
    updateVersionQuestions: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        versionId,
        ...payload
      }: RequestTypes['updateCompetencyTemplateVersionQuestions']) => {
        const { data } = await APIAxiosInstance.put(
          `competencies/admin/templates/${templateId}/versions/${versionId}/questions`,
          payload,
        )
        return data
      },
    },
    activateVersion: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        versionId,
      }: RequestTypes['activateCompetencyTemplateVersion']) => {
        const { data } = await APIAxiosInstance.put(
          `competencies/admin/templates/${templateId}/versions/${versionId}/activate`,
        )
        return data
      },
    },
    discardVersion: {
      mutationKey: null,
      mutationFn: async ({
        templateId,
        versionId,
      }: RequestTypes['discardCompetencyTemplateVersion']) => {
        const { data } = await APIAxiosInstance.put(
          `competencies/admin/templates/${templateId}/versions/${versionId}/discard`,
        )
        return data
      },
    },
  },
)
